import * as Sentry from '@sentry/nextjs';

export type SentryContext = {
  tags?: { [key: string]: string };
  level?: Sentry.SeverityLevel;
  contexts?: { [key: string]: any };
};

// For non-200, fetcher(...) adds `status` to the error object.
export type FetcherError = {
  status: number;
};

// Overload definitions
function captureSentryError(error: string, context?: SentryContext): void;
function captureSentryError(error: Error, context?: SentryContext): void;
function captureSentryError(error: FetcherError, context?: SentryContext): void;

// Function implementation
function captureSentryError(error: string | FetcherError | Error, context: SentryContext = {}): void {
  console.log('Logging error to Sentry:', { error, context });

  if (typeof error === 'string') {
    Sentry.captureMessage(error, {
      tags: context.tags,
      level: context.level || 'error',
      contexts: context.contexts
    });
  } else if (error instanceof Error || error?.status) {
    Sentry.withScope((scope) => {
      if (context.tags) {
        Object.entries(context.tags).forEach(([key, val]) => scope.setTag(key, val));
      }
      if (context.level) {
        scope.setLevel(context.level as Sentry.SeverityLevel);
      }
      if (context.contexts) {
        Object.entries(context.contexts).forEach(([key, val]) => scope.setContext(key, val));
      }
      Sentry.captureException(error);
    });
  } else {
    console.error('Invalid error type:', typeof error, error);
  }
}
const stringifyMessage = (message: string | object) => (typeof message === 'object' ? JSON.stringify(message) : message);

// This interface requires `category`, otherwise breadcrumb shows up as `generic` in Sentry.
export const SentryBreadcrumb = {
  info: ({ category, message, data }: { category: string; message: string | object; data?: any }) => {
    Sentry.addBreadcrumb({ level: 'info', category, message: stringifyMessage(message), data });
  },
  error: ({ category, message, data }: { category: string; message: string | object; data?: any }) => {
    Sentry.addBreadcrumb({ level: 'error', category, message: stringifyMessage(message), data });
  }
};

export { captureSentryError };
